import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Crown, Sparkles, Flame, Check, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/api";

const PLANS = [
  {
    id: "monthly",
    name: "Monthly",
    price: "$4.99",
    period: "/month",
    features: ["Unlimited analyses", "AI coaching"],
    icon: Sparkles,
  },
  {
    id: "yearly",
    name: "Yearly",
    price: "$29.99",
    period: "/year",
    subtext: "~$2.50/mo",
    features: ["Everything in Monthly", "Progress tracking", "Best value!"],
    icon: Crown,
    featured: true,
  },
  {
    id: "lifetime",
    name: "Lifetime",
    price: "$79",
    period: "one-time",
    features: ["Own it forever", "All future updates", "VIP support"],
    icon: Crown,
  },
];

export default function PaywallModal({ isOpen, onClose }) {
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckout = async () => {
    setIsLoading(true);

    try {
      const originUrl = window.location.origin;
      const { data } = await api.post("/checkout/session", {
        plan_type: selectedPlan,
        origin_url: originUrl,
      });
      window.location.href = data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Something went wrong. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          data-testid="paywall-modal"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-background rounded-3xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl relative"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="close-paywall-btn"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.1 }}
                className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 mb-4"
              >
                <Crown className="w-8 h-8 text-white" />
              </motion.div>
              
              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-2">
                Unlock your AI Nail Coach
              </h2>
              
              <p className="text-muted-foreground">
                Unlimited analyses, weekly coaching, progress tracking, streaks & badges 💅
              </p>
            </div>

            {/* Social proof */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-center justify-center gap-2 mb-6 text-sm"
            >
              <Users className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">
                Join <span className="font-semibold text-foreground">2,347 girls</span> already rating daily
              </span>
            </motion.div>

            {/* Pricing Cards */}
            <div className="space-y-3 mb-6">
              {PLANS.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`pricing-card relative p-4 rounded-2xl cursor-pointer border-2 transition-all ${
                    selectedPlan === plan.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  } ${plan.featured ? "featured" : ""}`}
                  data-testid={`plan-${plan.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        selectedPlan === plan.id 
                          ? "bg-primary text-white" 
                          : "bg-secondary text-primary"
                      }`}>
                        <plan.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{plan.name}</p>
                        {plan.subtext && (
                          <p className="text-xs text-muted-foreground">{plan.subtext}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-playfair text-xl font-bold text-foreground">
                        {plan.price}
                      </p>
                      <p className="text-xs text-muted-foreground">{plan.period}</p>
                    </div>
                  </div>
                  
                  {/* Features */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {plan.features.map((feature, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary/50 text-secondary-foreground"
                      >
                        <Check className="w-3 h-3" />
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* Selection indicator */}
                  <div className={`absolute top-4 right-4 w-5 h-5 rounded-full border-2 transition-all ${
                    selectedPlan === plan.id
                      ? "border-primary bg-primary"
                      : "border-border"
                  }`}>
                    {selectedPlan === plan.id && (
                      <Check className="w-4 h-4 text-white absolute top-0 left-0" />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Button */}
            <Button
              onClick={handleCheckout}
              disabled={isLoading}
              className="w-full rounded-full py-6 text-lg font-semibold bg-primary hover:bg-primary/90 shadow-soft hover:shadow-glow transition-all"
              data-testid="checkout-btn"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 animate-spin" />
                  Processing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Flame className="w-5 h-5" />
                  Unlock Now
                </span>
              )}
            </Button>

            {/* Trust badges */}
            <div className="mt-4 flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <span>🔒 Secure checkout</span>
              <span>•</span>
              <span>Cancel anytime</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
